import { useEffect, useState } from 'react';

const QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Live boolean for the user's `prefers-reduced-motion` setting.
 *
 * Returns `false` during SSR / pre-hydration so we never flash an
 * incorrect state. Subscribes to `change` so toggling the OS setting
 * in another window propagates without a reload.
 *
 * The boolean updates cause a re-render only when the value actually
 * flips, so consumers can safely use the return value inside JSX
 * without worrying about thrash.
 */
export function usePrefersReducedMotion(): boolean {
	const [prefersReducedMotion, setPrefersReducedMotion] = useState<boolean>(false);

	useEffect(() => {
		if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

		const mql = window.matchMedia(QUERY);
		setPrefersReducedMotion(mql.matches);

		const onChange = (event: MediaQueryListEvent): void => {
			setPrefersReducedMotion(event.matches);
		};

		mql.addEventListener('change', onChange);
		return () => {
			mql.removeEventListener('change', onChange);
		};
	}, []);

	return prefersReducedMotion;
}
